import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  FiBarChart2, FiShoppingCart, FiDollarSign, FiCreditCard, FiArchive,
  FiChevronDown, FiRefreshCw, FiDownload,
  FiMaximize2, FiMinimize2, FiGrid, FiCalendar, FiFileText, FiTrendingUp, FiClipboard, FiAlertTriangle,
  FiPieChart, FiList, FiHelpCircle, FiUsers, FiTruck,
  FiZap, FiUserPlus, FiPlusCircle, FiBookOpen, FiTag,
  FiShoppingBag, FiSend, FiClock, FiBriefcase
} from 'react-icons/fi';
import { colors, spacing, borderRadius, shadows, typography } from '../theme/colors';
import { useAuth } from '../context/AuthContext';
import { DashboardProvider, useDashboard } from '../context/DashboardContext';
import { ThemeProvider, useTheme } from '../context/ThemeContext';
import PageLayout from '../components/PageLayout';

// Dashboard Widgets (D2 set, 2026-07-17 — analytics widgets removed; their
// full gated reports live in the Reports menu)
import ExecutiveSummary from '../components/dashboard/ExecutiveSummary';
import KPICards from '../components/dashboard/KPICards';
import SalesOverviewWidget from '../components/dashboard/SalesOverviewWidget';
import SalesRecoveryWidget from '../components/dashboard/SalesRecoveryWidget';
import SalesExpenseWidget from '../components/dashboard/SalesExpenseWidget';
import MoneyPositionWidget from '../components/dashboard/MoneyPositionWidget';
import ReceivablePayableWidget from '../components/dashboard/ReceivablePayableWidget';
import { dashboardGridVariants, dashboardItemVariants, noMotionVariants } from '../theme/animations';
import ActivityFeedWidget from '../components/dashboard/ActivityFeedWidget';
import WhatsNewWidget from '../components/dashboard/WhatsNewWidget';

// Hooks
import useExportPDF from '../hooks/useExportPDF';

// Maps the kebab-case widget ids used in DashboardContext to the camelCase
// permission keys in the role matrix (UsersRoles → Dashboard Widgets).
const WIDGET_PERM_KEY = {
  'executive-summary': 'executiveSummary',
  'kpi-cards':         'kpiCards',
  'sales-overview':    'salesOverview',
  'sales-recovery':    'salesRecovery',
  'sales-expense':     'salesExpense',
  'money-position':    'moneyPosition',
  'receivable-payable':'receivablePayable',
  'activity-feed':     'activityFeed',
  'whats-new':         'whatsNew',
};

// When the six analytics widgets were removed in July, the three replacements
// reused the OLD widgets' permission keys rather than migrating role documents.
// The labels in the rights panel then said one thing while controlling another
// ("Cash Flow" actually hid Sales vs Recovery). The keys were corrected on
// 2026-08-06; this map keeps the old ones working during the changeover.
//
// Why it matters: hasPermission() treats a MISSING dashboard key as VISIBLE
// (AuthContext.js:579-583). So without this, a role that had deliberately
// hidden "Cash Flow" would suddenly START SHOWING Sales vs Recovery — the new
// key isn't in that role document, so it would read as allowed. Requiring BOTH
// keys means an explicit old "false" still hides the widget, while a role that
// never had the old key (missing → true) is unaffected.
//
// Safe to delete once every role has been re-saved under the new keys.
const LEGACY_WIDGET_PERM_KEY = {
  'sales-overview': 'salesIntelligence',
  'sales-recovery': 'cashFlow',
  'sales-expense':  'financialHealth',
};

const DashboardContent = () => {
  const { userProfile, hasPermission } = useAuth();
  // Respect the OS "reduce motion" setting — those users get the final layout
  // instantly, with no movement anywhere on the dashboard.
  const prefersReduced = useReducedMotion();
  const gridVariants = prefersReduced ? noMotionVariants : dashboardGridVariants;
  const itemVariants = prefersReduced ? noMotionVariants : dashboardItemVariants;
  // Role-gated visibility — checked on every widget render. Admin/Owner bypass
  // is built into hasPermission, so this returns true for those users.
  const canSeeWidget = React.useCallback(
    (kebab) => {
      if (!hasPermission('dashboard', WIDGET_PERM_KEY[kebab] || kebab, 'toggle')) return false;
      // Honour an explicit "hidden" saved under the widget's old key. A role
      // that never had that key reads as visible, so this only ever preserves a
      // deliberate hide — it can't take access away from anyone.
      const legacy = LEGACY_WIDGET_PERM_KEY[kebab];
      if (legacy && !hasPermission('dashboard', legacy, 'toggle')) return false;
      return true;
    },
    [hasPermission]
  );
  const navigate = useNavigate();
  const { toggleTheme, isDark } = useTheme();
  const {
    data,
    dateRange,
    changeDateRange,
    dateRangePresets,
    widgetVisibility,
    toggleWidget,
    refreshing,
    refreshDashboard
  } = useDashboard();

  const { exportToPDF, exporting, progress } = useExportPDF();

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dashboardRef = useRef(null);

  // D2: the business-health score and its inputs were removed with the
  // Financial Health widget (owner decision) — the data that fed them is no
  // longer downloaded.

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      dashboardRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Export dashboard
  const handleExport = useCallback(async () => {
    await exportToPDF('dashboard-content', {
      title: 'ERP Dashboard Report',
      companyName: 'AMAL ERP',
      includeHeader: true,
      includeFooter: true
    });
  }, [exportToPDF]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  if (!data) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Loading dashboard...</LoadingText>
      </LoadingContainer>
    );
  }

  const toolbarActions = (
    <>
      {/* Toolbar Buttons */}
      <ToolbarButton onClick={refreshDashboard} disabled={refreshing} title="Refresh Data">
        <FiRefreshCw className={refreshing ? 'spinning' : ''} />
      </ToolbarButton>

      <ToolbarButton onClick={() => setShowWidgetSettings(!showWidgetSettings)} title="Widget Settings">
        <FiGrid />
      </ToolbarButton>

      <ToolbarButton onClick={() => navigate('/help')} title="Help & User Manual">
        <FiHelpCircle />
      </ToolbarButton>
    </>
  );

  return (
    <PageLayout
      pageTitle="Dashboard"
      breadcrumb={`Last updated: ${format(new Date(), 'dd MMM yyyy, HH:mm')}`}
      headerActions={toolbarActions}
    >
      <div ref={dashboardRef}>
        {/* Widget Settings Panel */}
        <AnimatePresence>
          {showWidgetSettings && (
            <WidgetSettingsPanel
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <SettingsTitle>Widget Visibility</SettingsTitle>
              <WidgetToggles>
                {Object.entries(widgetVisibility)
                  // Hide widgets the user's role doesn't allow — they can't
                  // turn them back on anyway, so keep the panel uncluttered.
                  .filter(([widgetId]) => canSeeWidget(widgetId))
                  .map(([widgetId, visible]) => (
                    <WidgetToggle key={widgetId}>
                      <ToggleSwitch $active={visible} onClick={() => toggleWidget(widgetId)}>
                        <ToggleKnob $active={visible} />
                      </ToggleSwitch>
                      <ToggleLabel>{widgetId.replace(/-/g, ' ')}</ToggleLabel>
                    </WidgetToggle>
                  ))}
              </WidgetToggles>
            </WidgetSettingsPanel>
          )}
        </AnimatePresence>

        {/* Export Progress */}
        {exporting && (
          <ExportProgress>
            <ExportProgressBar style={{ width: `${progress}%` }} />
            <ExportText>Exporting PDF... {progress}%</ExportText>
          </ExportProgress>
        )}

        {/* Dashboard Content.
            The grid staggers its children in on mount (see animations.js).
            `initial`/`animate` here fire ONCE — data loading re-renders the
            widgets rather than remounting them, so figures arriving never
            replay the entrance. */}
        <div id="dashboard-content">
          <DashboardGrid
            as={motion.div}
            variants={gridVariants}
            initial="initial"
            animate="animate"
          >
            {/* Executive Summary - Full Width */}
            {canSeeWidget('executive-summary') && widgetVisibility['executive-summary'] && (
              <MGridItem variants={itemVariants} $colSpan={12}>
                <ExecutiveSummary
                  showHealthGauge={false}
                  userName={userProfile?.displayName || 'Admin'}
                  onThemeToggle={toggleTheme}
                  isDarkMode={isDark}
                />
              </MGridItem>
            )}

            {/* KPI strip — full width, four cards across. Reads the scoreboard
                document; Money Position and Receivable/Payable below reuse the
                very same read. */}
            {canSeeWidget('kpi-cards') && widgetVisibility['kpi-cards'] && (
              <MGridItem variants={itemVariants} $colSpan={12}>
                <KPICards />
              </MGridItem>
            )}

            {/* Quick Reports — hidden for now. Reports are still accessible
                from the left-nav Reports section. Re-enable by removing this
                {false && ...} wrapper. */}
            {false && (
            <MGridItem variants={itemVariants} $colSpan={12}>
              <ReportShortcutsCard>
                <ReportCardHeader>
                  <FiBarChart2 size={18} />
                  <ReportCardTitle>Quick Reports</ReportCardTitle>
                </ReportCardHeader>
                <ReportGrid>
                  {[
                    { icon: FiFileText, label: 'Account Ledger', path: '/reports/ledger', color: colors.primary.main },
                    { icon: FiList, label: 'Journal Report', path: '/reports/journal', color: '#6366F1' },
                    { icon: FiClipboard, label: 'Trial Balance', path: '/reports/trial-balance', color: '#0EA5E9' },
                    { icon: FiTrendingUp, label: 'Profit & Loss', path: '/reports/profit-loss', color: colors.success.main },
                    { icon: FiPieChart, label: 'Balance Sheet', path: '/reports/balance-sheet', color: '#8B5CF6' },
                    { icon: FiShoppingCart, label: 'Purchase Register', path: '/reports/purchase-register', color: '#F59E0B' },
                    { icon: FiDollarSign, label: 'Sale Register', path: '/reports/sale-register', color: '#EC4899' },
                    { icon: FiAlertTriangle, label: 'Low Stock Alert', path: '/reports/low-stock-alert', color: colors.error.main },
                    { icon: FiCreditCard, label: 'AP Aging', path: '/reports/ap-aging', color: '#D946EF' },
                    { icon: FiArchive, label: 'AR Aging', path: '/reports/ar-aging', color: '#F97316' },
                    { icon: FiUsers, label: 'Customer Balances', path: '/reports/customer-balances', color: '#0891B2' },
                    { icon: FiTruck, label: 'Vendor Balances', path: '/reports/vendor-balances', color: '#7C3AED' },
                  ].map((report) => (
                    <ReportShortcut
                      key={report.path}
                      as={motion.div}
                      whileHover={{ y: -2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => navigate(report.path)}
                      $color={report.color}
                    >
                      <ReportIconWrap $color={report.color}>
                        <report.icon size={16} />
                      </ReportIconWrap>
                      <ReportLabel>{report.label}</ReportLabel>
                    </ReportShortcut>
                  ))}
                </ReportGrid>
              </ReportShortcutsCard>
            </MGridItem>
            )}

            {/* Money Position + Receivable vs Payable — both read the SAME
                scoreboard document the KPI cards already loaded, so together
                they add ZERO Firestore reads. */}
            {canSeeWidget('money-position') && widgetVisibility['money-position'] !== false && (
              <MGridItem variants={itemVariants} $colSpan={8} $colSpanMd={12}>
                <MoneyPositionWidget />
              </MGridItem>
            )}

            {canSeeWidget('receivable-payable') && widgetVisibility['receivable-payable'] !== false && (
              <MGridItem variants={itemVariants} $colSpan={4} $colSpanMd={12}>
                <ReceivablePayableWidget />
              </MGridItem>
            )}

            {/* Sales vs Recovery */}
            {canSeeWidget('sales-recovery') && widgetVisibility['sales-recovery'] && (
              <MGridItem variants={itemVariants} $colSpan={6} $colSpanMd={12}>
                <SalesRecoveryWidget />
              </MGridItem>
            )}

            {/* Sales vs Expense */}
            {canSeeWidget('sales-expense') && widgetVisibility['sales-expense'] && (
              <MGridItem variants={itemVariants} $colSpan={6} $colSpanMd={12}>
                <SalesExpenseWidget />
              </MGridItem>
            )}

            {/* Sales Overview + right rail. Narrowed from full width — at 12
                columns its chart and Top-5 list dominated the page. */}
            {canSeeWidget('sales-overview') && widgetVisibility['sales-overview'] && (
              <MGridItem variants={itemVariants} $colSpan={8} $colSpanMd={12}>
                <SalesOverviewWidget />
              </MGridItem>
            )}

            <MGridItem variants={itemVariants} $colSpan={4} $colSpanMd={12}>
              {canSeeWidget('whats-new') && widgetVisibility['whats-new'] !== false && <WhatsNewWidget />}
              <QuickActionsCard>
                <QuickActionsHeader>
                  <FiZap size={16} style={{ color: '#F59E0B' }} />
                  <QuickActionsTitle>Quick Actions</QuickActionsTitle>
                </QuickActionsHeader>
                <QuickActionsList>
                  {/* Six, not twelve. A shortcut list long enough to need
                      scanning stops being a shortcut — these are the documents
                      people actually create daily; everything else is one click
                      away in the left navigation. */}
                  {[
                    { icon: FiSend, label: 'Sale Invoice', path: '/sales/sale-invoice', color: '#10B981' },
                    { icon: FiShoppingBag, label: 'Purchase Invoice', path: '/purchases/purchase-invoice', color: '#F97316' },
                    { icon: FiDollarSign, label: 'Cash Receipt', path: '/receipts/cash-receipt', color: '#22C55E' },
                    { icon: FiCreditCard, label: 'Cash Payment', path: '/payments/cash-payment', color: '#EF4444' },
                    { icon: FiUserPlus, label: 'Add Customer', path: '/master/customers', color: '#0EA5E9' },
                    { icon: FiTag, label: 'Add Item', path: '/master/chart-of-inventory', color: '#14B8A6' },
                  ].map((action) => (
                    <QuickActionItem
                      key={action.path}
                      as={motion.div}
                      whileHover={{ x: 4, backgroundColor: action.color + '08' }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate(action.path)}
                    >
                      <QuickActionIcon $color={action.color}>
                        <action.icon size={14} />
                      </QuickActionIcon>
                      <QuickActionLabel>{action.label}</QuickActionLabel>
                      <QuickActionArrow>
                        <FiChevronDown size={14} style={{ transform: 'rotate(-90deg)' }} />
                      </QuickActionArrow>
                    </QuickActionItem>
                  ))}
                </QuickActionsList>
              </QuickActionsCard>
            </MGridItem>

            {/* Recent activity last — it is reference, not a headline. */}
            {canSeeWidget('activity-feed') && widgetVisibility['activity-feed'] && (
              <MGridItem variants={itemVariants} $colSpan={12}>
                <ActivityFeedWidget />
              </MGridItem>
            )}

            {/* Activity Feed moved to left column alongside KPI Cards */}
          </DashboardGrid>
        </div>
      </div>
    </PageLayout>
  );
};

// Main Dashboard Component with Providers
const Dashboard = () => {
  return (
    <ThemeProvider>
      <DashboardProvider>
        <DashboardContent />
      </DashboardProvider>
    </ThemeProvider>
  );
};

// Dashboard-specific Styled Components

const DateRangeWrapper = styled.div`
  position: relative;
  z-index: 1000;
`;

const DateRangeBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
`;

const DateRangeButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  background: var(--color-background-default);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-light);
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary-main);
  }

  svg {
    font-size: 16px;
    color: var(--color-text-secondary);
  }
`;

const DateRangeDropdown = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${spacing.xs};
  background: var(--color-background-default);
  border: 1px solid var(--color-border-light);
  border-radius: ${borderRadius.md};
  box-shadow: var(--shadow-lg);
  z-index: 1000;
  min-width: 180px;
  overflow: hidden;
`;

const DateRangeOption = styled.button`
  display: block;
  width: 100%;
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.$active ? 'color-mix(in srgb, var(--color-primary-main) 6%, transparent)' : 'transparent'};
  color: ${props => props.$active ? 'var(--color-primary-main)' : 'var(--color-text-primary)'};
  border: none;
  font-size: ${typography.fontSize.sm};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-background-paper);
  }
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--color-background-default);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary-main);
    color: var(--color-primary-main);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 16px;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const WidgetSettingsPanel = styled(motion.div)`
  background: var(--color-background-default);
  border-bottom: 1px solid var(--color-border-light);
  padding: ${spacing.lg};
  overflow: hidden;
`;

const SettingsTitle = styled.h3`
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-primary);
  margin: 0 0 ${spacing.md} 0;
`;

const WidgetToggles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.md};
`;

const WidgetToggle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const ToggleSwitch = styled.button`
  position: relative;
  width: 44px;
  height: 24px;
  background: ${props => props.$active ? 'var(--color-primary-main)' : 'var(--color-neutral-300)'};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s ease;
`;

const ToggleKnob = styled.div`
  position: absolute;
  top: 2px;
  left: ${props => props.$active ? '22px' : '2px'};
  width: 20px;
  height: 20px;
  background: var(--color-text-white);
  border-radius: 50%;
  transition: left 0.2s ease;
  box-shadow: var(--shadow-sm);
`;

const ToggleLabel = styled.span`
  font-size: ${typography.fontSize.sm};
  color: var(--color-text-secondary);
  text-transform: capitalize;
`;

const ExportProgress = styled.div`
  position: fixed;
  bottom: ${spacing.lg};
  right: ${spacing.lg};
  background: var(--color-secondary-main);
  padding: ${spacing.md} ${spacing.lg};
  border-radius: ${borderRadius.md};
  box-shadow: var(--shadow-lg);
  z-index: 1000;
  min-width: 200px;
`;

const ExportProgressBar = styled.div`
  height: 4px;
  background: var(--color-primary-main);
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const ExportText = styled.div`
  font-size: ${typography.fontSize.sm};
  color: var(--color-text-white);
  margin-top: ${spacing.sm};
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: ${spacing.lg};

  @media (max-width: 1200px) {
    gap: ${spacing.md};
  }
`;

const GridItem = styled.div`
  grid-column: span ${props => props.$colSpan || 12};

  @media (max-width: 1200px) {
    grid-column: span ${props => props.$colSpanMd || props.$colSpan || 12};
  }

  @media (max-width: 768px) {
    grid-column: span 12;
  }
`;

// Motion-wrapped grid cell. Each one picks up `dashboardItemVariants` from the
// parent grid's stagger, so panels fade and rise in sequence rather than all
// snapping in at once. Wrapping the styled component (rather than using
// `as={motion.div}`) keeps the $colSpan props working.
const MGridItem = motion(GridItem);

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: var(--color-background-paper);
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid var(--color-neutral-200);
  border-top-color: var(--color-primary-main);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  margin-top: ${spacing.md};
  font-size: ${typography.fontSize.md};
  color: var(--color-text-secondary);
`;

// Quick Reports Styled Components
const ReportShortcutsCard = styled.div`
  background: var(--color-background-default);
  border-radius: ${borderRadius.md};
  padding: ${spacing.lg};
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border-light);
`;

const ReportCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.lg};
  color: var(--color-text-primary);
`;

const ReportCardTitle = styled.h3`
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-primary);
  margin: 0;
`;

const ReportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${spacing.md};

  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ReportShortcut = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.md} ${spacing.sm};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$color + '06'};
  border: 1px solid ${props => props.$color + '18'};

  &:hover {
    background: ${props => props.$color + '12'};
    border-color: ${props => props.$color + '30'};
  }
`;

const ReportIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${borderRadius.sm};
  background: ${props => props.$color + '15'};
  color: ${props => props.$color};
`;

const ReportLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: var(--color-text-secondary);
  text-align: center;
  line-height: 1.3;
`;

// Left column stacker (KPI + Activity Feed)
// Quick Actions (list-style sidebar)
const QuickActionsCard = styled.div`
  background: var(--color-background-default);
  border-radius: ${borderRadius.lg};
  padding: ${spacing.md};
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border-light);
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const QuickActionsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding-bottom: ${spacing.sm};
  margin-bottom: ${spacing.xs};
  border-bottom: 1px solid var(--color-border-light);
`;

const QuickActionsTitle = styled.h3`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-primary);
  margin: 0;
`;

const QuickActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  overflow-y: auto;
`;

const QuickActionItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: 8px ${spacing.sm};
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: var(--color-background-hover);
  }
`;

const QuickActionIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$color + '12'};
  color: ${props => props.$color};
  flex-shrink: 0;
`;

const QuickActionLabel = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: var(--color-text-primary);
  flex: 1;
`;

const QuickActionArrow = styled.span`
  color: var(--color-text-tertiary);
  display: flex;
  align-items: center;
  opacity: 0.5;
`;

export default Dashboard;
